import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Mail, RotateCw, Store } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { API } from '@/App';

const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric',
}) : '—');

const STATUS_LABELS = {
  new: 'New enquiry',
  contacted: 'Questionnaire sent',
  invited: 'Invited to sell',
  declined: 'Not proceeding',
};

/**
 * Who has asked to sell, and where each one stands.
 *
 * Nothing here creates an account. When an enquiry is worth taking forward, the Vendor Invite
 * on the Vendors tab is what opens a storefront — this is the conversation before that.
 */
export default function AdminVendorInterest() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vendor-interest/admin`);
      setRows(r.data.enquiries || []);
      setCounts(r.data.counts || {});
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await axios.patch(`${API}/vendor-interest/admin/${id}`, { status });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "That didn't save. Please try again.");
    } finally { setBusy(null); }
  };

  const resend = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/vendor-interest/admin/${id}/resend`);
      toast[r.data.emailed ? 'success' : 'warning'](
        r.data.emailed ? 'Questionnaire sent again' : 'Nothing was sent — check the address',
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "That didn't send. Please try again.");
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <p className="text-sm text-gray-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading enquiries…
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-vendor-interest">
      <div className="flex flex-wrap gap-3 text-xs text-gray-400">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <span key={key} data-testid={`vendor-interest-count-${key}`}>
            {label}: <span className="text-white">{counts[key] || 0}</span>
          </span>
        ))}
      </div>

      {!rows.length ? (
        <p className="text-sm text-gray-400" data-testid="vendor-interest-empty">
          Nobody has asked to sell yet. The Sell Here button in the Marketplace lands here.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/60">
              <tr className="text-left text-gray-300">
                <th className="p-3 font-medium">Business</th>
                <th className="p-3 font-medium">Who asked</th>
                <th className="p-3 font-medium">Asked</th>
                <th className="p-3 font-medium">Questionnaire sent</th>
                <th className="p-3 font-medium">Where it stands</th>
                <th className="p-3 font-medium">Next</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-800 align-top"
                    data-testid={`vendor-interest-row-${r.id}`}>
                  <td className="p-3">
                    <span className="text-white flex items-center gap-1.5">
                      <Store className="w-3.5 h-3.5 text-gray-500" /> {r.business_name}
                    </span>
                    {r.note && <span className="block text-xs text-gray-500 mt-1 max-w-xs">{r.note}</span>}
                  </td>
                  <td className="p-3 text-gray-300">
                    {r.full_name}
                    <span className="block text-xs text-gray-300">{r.email}</span>
                    {r.member_user_id && (
                      <span className="block text-[10px] text-cyan-300 mt-0.5">
                        already a member{r.member_tier ? ` · ${r.member_tier}` : ''} — an invite keeps their tier
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-gray-400 whitespace-nowrap">{when(r.created_at)}</td>
                  <td className="p-3 text-xs text-gray-400 whitespace-nowrap">
                    {r.followed_up_at
                      ? when(r.followed_up_at)
                      : <span className="text-amber-300">not sent</span>}
                  </td>
                  <td className="p-3 text-xs text-gray-300">{STATUS_LABELS[r.status] || r.status}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="ghost" disabled={busy === r.id}
                              onClick={() => resend(r.id)}
                              className="h-7 gap-1 border border-white/10 px-2 text-[11px] text-gray-300"
                              data-testid={`vendor-interest-resend-${r.id}`}>
                        <RotateCw className="w-3 h-3" /> Resend
                      </Button>
                      {r.status !== 'invited' && (
                        <Button size="sm" variant="ghost" disabled={busy === r.id}
                                onClick={() => setStatus(r.id, 'invited')}
                                className="h-7 gap-1 border border-emerald-400/30 px-2 text-[11px] text-emerald-200"
                                data-testid={`vendor-interest-invited-${r.id}`}>
                          <Mail className="w-3 h-3" /> Mark invited
                        </Button>
                      )}
                      {r.status !== 'declined' && (
                        <Button size="sm" variant="ghost" disabled={busy === r.id}
                                onClick={() => setStatus(r.id, 'declined')}
                                className="h-7 px-2 text-[11px] text-gray-200 border border-white/10"
                                data-testid={`vendor-interest-declined-${r.id}`}>
                          Not proceeding
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-500 max-w-3xl">
        Everyone here was emailed the Vendor Terms, the Marketplace Guidelines and the fit
        questionnaire when they asked. Marking someone invited is a note to yourself — the actual
        invitation is sent from the vendor invites list, and for an existing member it keeps their
        current tier and membership untouched.
      </p>
    </div>
  );
}
